import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import Swal from 'sweetalert2';
import {
  fetchSchoolInfo,
  fetchActivities,
  fetchTahuns,
  fetchStudents,
  // saveUniform and submitUniform moved to hooks/useUniformHandlers.js
  setSelectedActivity,
  setSelectedTahun,
  updateStudent,
  deleteStudent,
  restoreStudent,
  importValidatedStudents,
  selectSchoolInfo,
  selectActivities,
  selectTahuns,
  selectStudents,
  selectSizeConfig,
  selectStatus,
  selectSelectedActivity,
  selectSelectedTahun,
  selectTotalStudents,
  selectMaleStudents,
  selectFemaleStudents,
  selectHasUnsavedChanges,
  selectIsLoading,
} from '../../store/slices/uniformSlice';
import ImportModal from './ImportModal';
import ImportPreviewModal from './ImportPreviewModal';
import ReportIssueModal from './ReportIssueModal';
import ClassGroupingModal from './ClassGroupingModal';
import TutorialModal from './TutorialModal';
import AddStudentModal from './AddStudentModal';
import html2canvas from 'html2canvas';
import { studentUniformAPI } from '../../services/api';
import { getCellClass, getCellStyle } from './utils/uniformHelpers';
import { useUniformHandlers } from './hooks/useUniformHandlers';
import { useUnsavedChangesWarning } from '../../hooks/useUnsavedChangesWarning';
import './StudentUniform.css';

// Register AG Grid modules (required for AG Grid v31+)
ModuleRegistry.registerModules([AllCommunityModule]);

const StudentUniform = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation(['studentUniform', 'common']);

  // Redux state
  const schoolInfo = useSelector(selectSchoolInfo);
  const activities = useSelector(selectActivities);
  const tahuns = useSelector(selectTahuns);
  const students = useSelector(selectStudents);
  const sizeConfig = useSelector(selectSizeConfig);
  const status = useSelector(selectStatus);
  const selectedActivity = useSelector(selectSelectedActivity);
  const selectedTahun = useSelector(selectSelectedTahun);
  const totalStudents = useSelector(selectTotalStudents);
  const maleStudents = useSelector(selectMaleStudents);
  const femaleStudents = useSelector(selectFemaleStudents);
  const hasUnsavedChanges = useSelector(selectHasUnsavedChanges);
  const isLoading = useSelector(selectIsLoading);

  // Local state for AG-Grid
  const [gridApi, setGridApi] = useState(null);

  // Track scroll position to prevent jumping after edits
  const scrollPositionRef = React.useRef({ top: 0, left: 0 });

  // Context menu state for delete action
  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    studentId: null,
    studentName: ''
  });

  // Import modal state
  const [showImportModal, setShowImportModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [validationResult, setValidationResult] = useState(null);

  // Class grouping modal state
  const [showClassGroupingModal, setShowClassGroupingModal] = useState(false);

  // Tutorial modal state
  const [showTutorialModal, setShowTutorialModal] = useState(false);

  // Add student modal state
  const [showAddStudentModal, setShowAddStudentModal] = useState(false);

  // Report issue modal state
  const [showReportIssueModal, setShowReportIssueModal] = useState(false);
  const [screenshot, setScreenshot] = useState(null);

  // Tahun and Kelas dropdown lists for editing
  const [tahunList, setTahunList] = useState([]);
  const [kelasList, setKelasList] = useState([]);

  // Use refactored uniform handlers hook (fixes Save/Submit duplication)
  const { handleSave: handleSaveFromHook, handleSubmit: handleSubmitFromHook } = useUniformHandlers();

  // Warn user about unsaved changes when trying to leave the page
  useUnsavedChangesWarning(
    hasUnsavedChanges,
    t('studentUniform:unsavedChangesWarning', {
      defaultValue: 'You have unsaved changes in the student uniform data. If you leave now, all your changes will be lost. Are you sure you want to leave?'
    })
  );

  // Load school info, activities and tahuns on mount
  useEffect(() => {
    dispatch(fetchSchoolInfo());
    dispatch(fetchActivities());
    dispatch(fetchTahuns());

    // Load Tahun and Kelas lists for dropdown editing
    const loadDropdownLists = async () => {
      try {
        const [tahunData, kelasData] = await Promise.all([
          studentUniformAPI.getTahunList(),
          studentUniformAPI.getKelasList()
        ]);

        setTahunList(tahunData.data || []);
        setKelasList(kelasData.data || []);

        console.log('StudentUniform: Loaded dropdown lists:', {
          tahuns: tahunData.data?.length || 0,
          kelas: kelasData.data?.length || 0
        });
      } catch (error) {
        console.error('Error loading dropdown lists:', error);
      }
    };

    loadDropdownLists();
  }, [dispatch]);

  // Load students when activity and tahun are selected
  useEffect(() => {
    console.log('StudentUniform: useEffect triggered', { selectedActivity, selectedTahun });
    if (selectedActivity && selectedTahun) {
      console.log('StudentUniform: Fetching students...', {
        activityId: selectedActivity.activityId,
        tahun: selectedTahun
      });
      dispatch(fetchStudents({
        activityId: selectedActivity.activityId,
        tahun: selectedTahun
      }));
    } else {
      console.log('StudentUniform: NOT fetching - missing activity or tahun');
    }
  }, [dispatch, selectedActivity, selectedTahun]);

  // Handle activity selection
  const handleActivityChange = (e) => {
    const activityId = parseInt(e.target.value);
    const activity = activities.find(a => a.activityId === activityId);
    dispatch(setSelectedActivity(activity || null));
  };

  // Handle tahun selection
  const handleTahunChange = (e) => {
    dispatch(setSelectedTahun(e.target.value));
  };

  // Show context menu for delete action
  const handleDeleteStudent = (event, studentId, studentName) => {
    event.preventDefault();
    event.stopPropagation();

    // Bug #39 fix: Position menu on the LEFT side of the click
    // Subtract menu width (approximately 220px) to show on left
    const menuWidth = 220;
    const menuX = event.pageX - menuWidth;

    setContextMenu({
      visible: true,
      x: menuX,
      y: event.pageY,
      studentId,
      studentName
    });
  };

  // Handle reason selection from context menu
  const handleReasonSelect = async (reason) => {
    const { studentId, studentName } = contextMenu;
    setContextMenu({ ...contextMenu, visible: false });

    if (reason === 'OTHER') {
      // Show SweetAlert for custom reason
      const result = await Swal.fire({
        title: t('studentUniform:removalDialog.title'),
        input: 'text',
        inputPlaceholder: t('studentUniform:removalDialog.placeholder'),
        showCancelButton: true,
        confirmButtonText: t('studentUniform:removalDialog.confirm'),
        cancelButtonText: t('studentUniform:removalDialog.cancel')
      });

      if (result.isConfirmed && result.value) {
        dispatch(deleteStudent({ studentId, reason: `OTHER: ${result.value}` }));
      }
    } else {
      dispatch(deleteStudent({ studentId, reason }));
    }
  };

  // Close context menu
  useEffect(() => {
    const handleClick = () => setContextMenu({ ...contextMenu, visible: false });
    if (contextMenu.visible) {
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [contextMenu.visible]);

  // Handle restore deleted student
  const handleRestoreStudent = (studentId) => {
    dispatch(restoreStudent(studentId));
    // No popup needed - visual feedback is enough (row color change)
  };

  // Check if cell can be edited (based on status permissions)
  const canEditCell = (params) => {
    return status?.canEdit && !params.data.isDeleted;
  };

  // Check if a specific uniform field is editable based on gender and mutual exclusivity rules
  const isFieldEditable = (params, field) => {
    if (!canEditCell(params)) return false;

    const rowData = params.data;
    const gender = rowData.jantina;

    // ✅ OPTION 1: IC-Only Entry - Disable all uniform fields for new students until IC lookup completes
    // New students (isNewStudent === true) have no Jantina yet, so disable all uniform fields
    if (rowData.isNewStudent === true) {
      return false;  // Disable all uniform fields until IC lookup fills Jantina
    }

    if (gender === 'L') {
      // Male students
      // Can't edit female uniform fields
      if (['bajuKurung', 'kainKurung', 'pinafore'].includes(field)) {
        return false;
      }
      // Male Mutual Exclusivity: SeluarPanjang XOR SeluarPendek
      if (field === 'seluarPanjang' && rowData.seluarPendek) {
        return false; // SeluarPendek selected, can't edit SeluarPanjang
      }
      if (field === 'seluarPendek' && rowData.seluarPanjang) {
        return false; // SeluarPanjang selected, can't edit SeluarPendek
      }
    } else if (gender === 'P') {
      // Female students
      // Can't edit male uniform fields
      if (['seluarPanjang', 'seluarPendek'].includes(field)) {
        return false;
      }
      // Female Mutual Exclusivity: Set 1 vs Set 2
      const hasSet1 = rowData.bajuPutih || rowData.pinafore;
      const hasSet2 = rowData.bajuKurung || rowData.kainKurung;

      if (hasSet1 && ['bajuKurung', 'kainKurung'].includes(field)) {
        return false; // Set 1 selected, can't edit Set 2
      }
      if (hasSet2 && ['bajuPutih', 'pinafore'].includes(field)) {
        return false; // Set 2 selected, can't edit Set 1
      }
    }

    return true;
  };

  // AG-Grid column definitions - MATCHING OLD UI EXACTLY
  // Use useMemo to ensure column definitions update when dependencies change
  const columnDefs = useMemo(() => {
    const baseColumns = [
      {
        headerName: t('studentUniform:columns.no'),
        valueGetter: 'node.rowIndex + 1',
        width: 60,
        pinned: 'left',
      },
      {
        headerName: t('studentUniform:columns.name'),
        field: 'name',
        width: 257,  // Bug #39 fix: adjusted width
        pinned: 'left',
        editable: false,  // Always read-only (auto-filled from database lookup)
        cellStyle: (params) => {
          // Gray background for new students (read-only field)
          if (params.data.isNewStudent === true) {
            return { backgroundColor: '#e9ecef' };
          }
          return {};
        },
      },
      {
        headerName: t('studentUniform:columns.noKP'),
        field: 'noKP',  // .NET sends as camelCase in JSON - FULL 12-digit IC (stored in data)
        width: 150,
        pinned: 'left',
        editable: (params) => {
          // Only editable for new students (manually added rows)
          return status?.canEdit && params.data.isNewStudent === true;
        },
        cellStyle: (params) => {
          const baseStyle = {
            fontFamily: 'monospace',
            textAlign: 'center',
            fontWeight: 'normal'
          };
          // Highlight editable cells for new students
          if (params.data.isNewStudent === true) {
            return { ...baseStyle, backgroundColor: '#fff3cd' };
          }
          return baseStyle;
        },
        valueFormatter: (params) => {
          // ✅ IC DISPLAY: Backend handles masking based on authorization permissions
          // - FULL access (HEADMASTER/TEACHER): Backend sends full 12-digit IC (e.g., "140705102088")
          // - MASKED access (PPD/KTC): Backend sends masked IC (e.g., "********2088")
          // Frontend just formats with dashes for display
          if (!params.value || params.value === 'No IC') return params.data.isNewStudent ? '(Enter IC)' : 'No IC';

          // If IC is already masked (starts with asterisks), format as ******-**-XXXX
          if (params.value.startsWith('********')) {
            const last4 = params.value.substring(8, 12);
            return `******-**-${last4}`;
          }

          // If IC is full 12 digits, format as YYMMDD-PB-NNNN
          if (params.value.length === 12 && /^\d{12}$/.test(params.value)) {
            const yymmdd = params.value.substring(0, 6);
            const pb = params.value.substring(6, 8);
            const nnnn = params.value.substring(8, 12);
            return `${yymmdd}-${pb}-${nnnn}`;
          }

          // Otherwise show as-is (fallback)
          return params.value;
        },
        valueSetter: (params) => {
          // Remove all non-digits and dashes
          const cleanValue = params.newValue.replace(/[^\d-]/g, '').replace(/-/g, '');

          // Validate: must be 12 digits
          if (cleanValue && (cleanValue.length !== 12 || !/^\d{12}$/.test(cleanValue))) {
            console.warn('Invalid IC format. Must be 12 digits.');
            return false; // Reject invalid input
          }

          params.data.noKP = cleanValue;
          params.data.isEdited = true;
          return true;
        },
      },
      {
        headerName: t('studentUniform:columns.citizenStatus'),
        field: 'citizenStatus',
        width: 141,  // Bug #39 fix: adjusted width
        editable: false,  // Always read-only (auto-filled from database lookup)
        cellStyle: (params) => {
          const baseStyle = { textAlign: 'center' };
          // Gray background for new students (read-only field)
          if (params.data.isNewStudent === true) {
            return { ...baseStyle, backgroundColor: '#e9ecef' };
          }
          return baseStyle;
        },
        wrapHeaderText: true,
        autoHeaderHeight: true,
      },
      {
        headerName: t('studentUniform:columns.tahun'),
        field: 'tahun',  // Store in field for editing
        width: 98,  // Bug #39 fix: adjusted width
        editable: canEditCell,
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: {
          values: ['', ...tahunList],  // Add blank option + tahun list from database
        },
        valueGetter: (params) => params.data.tahun || selectedTahun,  // Use student's tahun or selected tahun
        valueSetter: (params) => {
          // CRITICAL: Update the tahun field when edited
          if (params.newValue !== params.oldValue) {
            params.data.tahun = params.newValue;
            params.data.isEdited = true;  // Mark as edited
            console.log(`StudentUniform: Tahun changed for student ${params.data.studentId}: ${params.oldValue} → ${params.newValue}`);
            return true;
          }
          return false;
        },
        cellStyle: { textAlign: 'center' },
      },
      {
        headerName: t('studentUniform:columns.kelas'),
        field: 'class',
        width: 120,  // Bug #39 fix: adjusted width
        editable: canEditCell,
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: {
          values: ['', ...kelasList],  // Add blank option + kelas list from database
        },
        cellStyle: { textAlign: 'center' },
      },
      {
        headerName: t('studentUniform:columns.jantina'),
        field: 'jantina',
        width: 105,  // Bug #39 fix: adjusted width
        editable: false,  // Always read-only (auto-filled from database lookup)
        cellRenderer: (params) => {
          // OLD UI: Just show L or P, no emoji
          return params.value || '';
        },
        cellStyle: (params) => {
          // Gray background for new students (read-only field)
          if (params.data.isNewStudent === true) {
            return { backgroundColor: '#e9ecef' };
          }
          return {};
        },
      },
    ];

    // Add uniform size columns
    const uniformColumns = [
      {
        headerName: t('studentUniform:columns.bajuPutih'),
        field: 'bajuPutih',
        width: 90,
        editable: (params) => isFieldEditable(params, 'bajuPutih'),
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: {
          values: ['', ...(sizeConfig?.uniformSizes || [])],  // Add blank option
        },
        cellClass: (params) => getCellClass(params),
        cellStyle: (params) => getCellStyle(params),
        wrapHeaderText: true,
        autoHeaderHeight: true,
      },
    ];

    // Add gender-specific columns
    if (sizeConfig?.uniformSizes) {
      // Male-only columns
      uniformColumns.push(
        {
          headerName: t('studentUniform:columns.seluarPanjang'),
          wrapHeaderText: true,
          autoHeaderHeight: true,
          field: 'seluarPanjang',
          width: 107,  // Bug #39 fix: adjusted width
          editable: (params) => isFieldEditable(params, 'seluarPanjang'),
          cellEditor: 'agSelectCellEditor',
          cellEditorParams: {
            values: ['', ...sizeConfig.uniformSizes],  // Add blank option
          },
          cellClass: (params) => getCellClass(params),
          cellStyle: (params) => getCellStyle(params),
        },
        {
          headerName: t('studentUniform:columns.seluarPendek'),
          field: 'seluarPendek',
          width: 105,  // Bug #39 fix: adjusted width
          editable: (params) => isFieldEditable(params, 'seluarPendek'),
          cellEditor: 'agSelectCellEditor',
          cellEditorParams: {
            values: ['', ...sizeConfig.uniformSizes],  // Add blank option
          },
          cellClass: (params) => getCellClass(params),
          cellStyle: (params) => getCellStyle(params),
          wrapHeaderText: true,
          autoHeaderHeight: true,
        }
      );

      // Female-only columns
      uniformColumns.push(
        {
          headerName: t('studentUniform:columns.bajuKurung'),
          field: 'bajuKurung',
          width: 107,  // Bug #39 fix: adjusted width
          editable: (params) => isFieldEditable(params, 'bajuKurung'),
          cellEditor: 'agSelectCellEditor',
          cellEditorParams: {
            values: ['', ...sizeConfig.uniformSizes],  // Add blank option
          },
          cellClass: (params) => getCellClass(params),
          cellStyle: (params) => getCellStyle(params),
          wrapHeaderText: true,
          autoHeaderHeight: true,
        },
        {
          headerName: t('studentUniform:columns.kainKurung'),
          field: 'kainKurung',
          width: 107,  // Bug #39 fix: adjusted width
          editable: (params) => isFieldEditable(params, 'kainKurung'),
          cellEditor: 'agSelectCellEditor',
          cellEditorParams: {
            values: ['', ...sizeConfig.uniformSizes],  // Add blank option
          },
          cellClass: (params) => getCellClass(params),
          cellStyle: (params) => getCellStyle(params),
          wrapHeaderText: true,
          autoHeaderHeight: true,
        },
        {
          headerName: t('studentUniform:columns.pinafore'),
          field: 'pinafore',
          width: 107,  // Bug #39 fix: adjusted width
          editable: (params) => isFieldEditable(params, 'pinafore'),
          cellEditor: 'agSelectCellEditor',
          cellEditorParams: {
            values: ['', ...sizeConfig.uniformSizes],  // Add blank option
          },
          cellClass: (params) => getCellClass(params),
          cellStyle: (params) => getCellStyle(params),
        }
      );
    }

    // Add shoe size column
    if (sizeConfig?.shoeSizes) {
      uniformColumns.push({
        headerName: t('studentUniform:columns.kasut'),
        field: 'kasut',
        width: 98,  // Bug #39 fix: adjusted width
        editable: (params) => canEditCell(params),
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: {
          values: ['', ...sizeConfig.shoeSizes],  // Add blank option
        },
        cellStyle: (params) => getCellStyle(params),
      });
    }

    // Add Action column (Delete/Restore button) - matching old UI
    const actionColumn = {
      headerName: t('studentUniform:columns.actions'),
      field: 'action',
      width: 100,
      pinned: 'right',
      cellStyle: { textAlign: 'center' },  // Bug #39 fix: center align trash icon
      cellRenderer: (params) => {
        const student = params.data;
        if (!student) return null;

        // ✅ UNDO DELETE FEATURE: Show restore icon for deleted students
        if (student.isDeleted) {
          return (
            <i
              className="fas fa-undo"
              style={{
                cursor: 'pointer',
                color: '#007bff',
                fontSize: '16px'
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleRestoreStudent(student.studentId);
              }}
              title={t('studentUniform:tooltips.restoreStudent')}
            />
          );
        }

        // Normal student - show trash icon (matching old UI)
        return (
          <i
            className="fas fa-trash"
            style={{
              cursor: 'pointer',
              color: '#dc3545',
              fontSize: '16px'
            }}
            onClick={(e) => handleDeleteStudent(e, student.studentId, student.name)}
            title={t('studentUniform:tooltips.removeStudent')}
          />
        );
      },
    };

    return [...baseColumns, ...uniformColumns, actionColumn];
  }, [status, tahunList, kelasList, sizeConfig, selectedTahun, t]);

  // NOTE: getCellClass and getCellStyle are now imported from utils/uniformHelpers.js

  // ===== AG-GRID HANDLERS =====

  /**
   * Handle cell click to start editing immediately (single-click behavior)
   */
  const onCellClicked = (params) => {
    const { colDef, api, node, column } = params;

    console.log('StudentUniform: Cell clicked -', column.getColId());

    // Check if cell is editable
    const isEditable = typeof colDef.editable === 'function'
      ? colDef.editable(params)
      : colDef.editable;

    // Only proceed if cell is editable and has a cell editor (dropdown)
    if (!isEditable || !colDef.cellEditor) {
      console.log('StudentUniform: Cell not editable or no editor -', {isEditable, hasCellEditor: !!colDef.cellEditor});
      return;
    }

    // Check if this cell is already being edited
    const editingCells = api.getEditingCells();
    const isThisCellEditing = editingCells.some(
      cell => cell.rowIndex === node.rowIndex && cell.column.getId() === column.getId()
    );

    // If not already editing, start editing immediately
    if (!isThisCellEditing) {
      console.log('StudentUniform: Starting edit on first click for column:', column.getColId());
      api.startEditingCell({
        rowIndex: node.rowIndex,
        colKey: column.getId()
      });
    }
  };

  /**
   * Save scroll position when editing starts
   * This prevents screen from jumping to top after editing
   * Also auto-opens dropdown selects for single-click editing
   */
  const onCellEditingStarted = (params) => {
    if (params.api) {
      const gridBodyCon = document.querySelector('.ag-body-viewport');
      if (gridBodyCon) {
        scrollPositionRef.current = {
          top: gridBodyCon.scrollTop,
          left: gridBodyCon.scrollLeft
        };
      }
    }

    // Auto-open dropdown for select cell editors (single-click behavior)
    const colDef = params.colDef;
    if (colDef.cellEditor === 'agSelectCellEditor') {
      console.log('StudentUniform: Edit started for select cell');

      // Use MutationObserver to watch for when the select element appears
      const gridContainer = document.querySelector('.ag-root');
      if (!gridContainer) {
        console.log('StudentUniform: Grid container not found');
        return;
      }

      let observerTimeout;
      let mutationCount = 0;
      const observer = new MutationObserver((mutations) => {
        mutationCount++;

        // Look for the ag-Grid custom select editor (div-based, not native select)
        const customSelectEditor = gridContainer.querySelector('.ag-cell-editor.ag-select');

        if (customSelectEditor) {
          console.log('StudentUniform: ✅ ag-Grid custom select editor found!');

          // Disconnect observer immediately
          observer.disconnect();
          clearTimeout(observerTimeout);

          // Try multiple approaches to open the dropdown
          setTimeout(() => {
            // Approach 1: Look for the wrapper or input element inside
            const wrapper = customSelectEditor.querySelector('.ag-wrapper');
            const selectInput = customSelectEditor.querySelector('select');
            const inputElement = customSelectEditor.querySelector('input');

            console.log('StudentUniform: Found elements -', {
              wrapper: !!wrapper,
              selectInput: !!selectInput,
              inputElement: !!inputElement,
              editorClasses: customSelectEditor.className
            });

            // Approach 2: Try to trigger mousedown event (this often opens dropdowns)
            const mousedownEvent = new MouseEvent('mousedown', {
              bubbles: true,
              cancelable: true,
              view: window,
              detail: 1
            });

            const clickEvent = new MouseEvent('click', {
              bubbles: true,
              cancelable: true,
              view: window,
              detail: 1
            });

            // Try clicking the most specific element we can find
            let clickTarget = wrapper || selectInput || inputElement || customSelectEditor;

            clickTarget.dispatchEvent(mousedownEvent);
            setTimeout(() => {
              clickTarget.dispatchEvent(clickEvent);
              console.log('StudentUniform: ✅ Dispatched mouse events to', clickTarget.tagName);
            }, 10);

            // Approach 3: Also try focusing the element
            if (inputElement) {
              inputElement.focus();
              console.log('StudentUniform: ✅ Focused input element');
            } else if (selectInput) {
              selectInput.focus();
              console.log('StudentUniform: ✅ Focused select element');
            } else {
              customSelectEditor.focus();
              console.log('StudentUniform: ✅ Focused editor div');
            }
          }, 50);
        }
      });

      // Start observing
      observer.observe(gridContainer, {
        childList: true,
        subtree: true
      });

      // Stop observing after 500ms (cleanup)
      observerTimeout = setTimeout(() => {
        observer.disconnect();
        console.log('StudentUniform: Observer timeout - custom select editor did not appear. Mutations:', mutationCount);
      }, 500);
    }
  };

  /**
   * Restore scroll position when editing stops
   * This prevents screen from jumping to top after editing
   */
  const onCellEditingStopped = (params) => {
    if (params.api) {
      // Use setTimeout to ensure DOM has updated
      setTimeout(() => {
        const gridBodyCon = document.querySelector('.ag-body-viewport');
        if (gridBodyCon && scrollPositionRef.current) {
          gridBodyCon.scrollTop = scrollPositionRef.current.top;
          gridBodyCon.scrollLeft = scrollPositionRef.current.left;
        }
      }, 0);
    }
  };

  // Handle cell value changed
  const onCellValueChanged = async (params) => {
    const { data, colDef, newValue, api } = params;
    const field = colDef.field;

    dispatch(updateStudent({
      studentId: data.studentId,
      field: field,
      value: newValue,
    }));

    // IMPORTANT: Refresh cells after uniform selection changes to apply mutual exclusivity rules
    // This ensures disabled fields are grayed out immediately
    const uniformFields = ['bajuPutih', 'seluarPanjang', 'seluarPendek', 'bajuKurung', 'kainKurung', 'pinafore'];
    if (uniformFields.includes(field)) {
      // Refresh the entire row to update all cell styles
      api.refreshCells({
        rowNodes: [params.node],
        force: true, // Force refresh even if data hasn't changed
      });
    }
  };

  // NOTE: filterModifiedData, buildValidationErrorHtml, and validateUniformData
  // are now in utils/uniformHelpers.js and used by hooks/useUniformHandlers.js

  // ===== IMPORT HANDLERS =====

  // Handle Import button click
  const handleImportClick = () => {
    if (!selectedActivity || !selectedTahun) {
      Swal.fire(t('common:error'), t('studentUniform:errors.selectActivityTahun'), 'error');
      return;
    }

    setShowImportModal(true);
  };

  // Handle Add New Student Row button click - Opens modal for IC lookup
  const handleAddNewStudentRow = () => {
    if (!selectedActivity || !selectedTahun) {
      Swal.fire(t('common:error'), t('studentUniform:errors.selectActivityTahun'), 'error');
      return;
    }

    // Open the Add Student modal
    setShowAddStudentModal(true);
  };

  // Handle confirmed student from modal - Add to grid
  const handleConfirmAddStudent = (newStudent) => {
    console.log('StudentUniform: Adding confirmed student to grid:', newStudent);

    // Dispatch action to add student to Redux store
    // Redux will update the students array, which will automatically update AG Grid via rowData binding
    dispatch(importValidatedStudents([newStudent]));

    console.log('StudentUniform: Student dispatched to Redux, grid will update automatically');
  };

  // Handle data parsed from ImportModal - send to backend for validation
  const handleDataParsed = async (parsedData) => {
    try {
      console.log('StudentUniform: Sending to backend for validation:', parsedData);

      // Prepare request payload
      const requestPayload = {
        method: parsedData.method,
        activityId: selectedActivity.activityId,
        tahun: selectedTahun,
        students: parsedData.students
      };

      // Call backend API for validation
      const response = await studentUniformAPI.importStudents(requestPayload);

      console.log('StudentUniform: Backend validation response:', response);

      if (response.success) {
        // Close import modal and show preview modal with validation results
        setShowImportModal(false);
        setValidationResult(response.data);
        setShowPreviewModal(true);
      } else {
        throw new Error(response.message || 'Validation failed');
      }
    } catch (error) {
      console.error('StudentUniform: Import validation error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Validation Failed',
        text: error.response?.data?.message || error.message || 'Failed to validate import data',
        confirmButtonText: 'OK'
      });
    }
  };

  // Handle confirm import - merge validated students into AG-Grid
  const handleConfirmImport = (validatedStudents) => {
    console.log('StudentUniform: Confirming import of', validatedStudents.length, 'students');

    // Check if this is a partial import (valid students only)
    const originalCount = validationResult?.students?.length || 0;
    const isPartialImport = validatedStudents.length < originalCount;

    // Calculate skipped students (if partial import)
    let skippedStudents = [];
    if (isPartialImport && validationResult?.students) {
      const importedICs = new Set(validatedStudents.map(s => s.noKP));
      skippedStudents = validationResult.students
        .filter(s => !importedICs.has(s.noKP))
        .map(s => ({
          name: s.name,
          noKP: s.noKP,
          issues: s.issues?.filter(i => i.type === 'error').map(i => i.message).join('; ') || 'Unknown error'
        }));
    }

    // Close preview modal
    setShowPreviewModal(false);
    setValidationResult(null);

    // Count actions for success message
    const addedCount = validatedStudents.filter(s => s.importAction === 'ADD').length;
    const updatedCount = validatedStudents.filter(s => s.importAction === 'UPDATE').length;

    // Dispatch Redux action to merge students into state
    dispatch(importValidatedStudents(validatedStudents));

    // Build success message HTML
    let successHtml = `
      <div style="text-align: left; padding: 10px;">
        <p><strong>✅ Students imported successfully!</strong></p>
        <ul style="margin-top: 10px;">
          <li>Added: ${addedCount} new student(s)</li>
          <li>Updated: ${updatedCount} existing student(s)</li>
          <li>Total Imported: ${validatedStudents.length} student(s)</li>
        </ul>
    `;

    // Add skipped students section if partial import
    if (isPartialImport && skippedStudents.length > 0) {
      const skippedRows = skippedStudents.map(s => `
        <tr style="background: #ffebee;">
          <td style="padding: 6px; border-bottom: 1px solid #ddd; font-size: 12px;">${s.name}</td>
          <td style="padding: 6px; border-bottom: 1px solid #ddd; font-size: 12px;">${s.noKP}</td>
          <td style="padding: 6px; border-bottom: 1px solid #ddd; color: #d32f2f; font-size: 11px;">${s.issues}</td>
        </tr>
      `).join('');

      successHtml += `
        <div style="margin-top: 20px; background: #fff3e0; border-left: 4px solid #ff9800; padding: 12px; border-radius: 4px;">
          <p style="color: #e65100; font-weight: 600; margin-bottom: 10px;">
            ⚠️ ${skippedStudents.length} student(s) skipped due to errors:
          </p>
          <div style="max-height: 200px; overflow-y: auto; background: white; border-radius: 4px; padding: 8px;">
            <table style="width: 100%; border-collapse: collapse; font-size: 12px;">
              <thead>
                <tr style="background: #f5f5f5;">
                  <th style="padding: 6px; border-bottom: 2px solid #ddd; text-align: left; font-size: 12px;">Name</th>
                  <th style="padding: 6px; border-bottom: 2px solid #ddd; text-align: left; font-size: 12px;">IC Number</th>
                  <th style="padding: 6px; border-bottom: 2px solid #ddd; text-align: left; font-size: 12px;">Errors</th>
                </tr>
              </thead>
              <tbody>${skippedRows}</tbody>
            </table>
          </div>
          <p style="margin-top: 8px; color: #e65100; font-size: 12px;">
            You can re-import these students after fixing the errors.
          </p>
        </div>
      `;
    }

    successHtml += `
        <p style="margin-top: 15px; color: #f57f17;">
          <strong>⚠️ Remember to Save or Submit</strong> to persist the changes.
        </p>
      </div>
    `;

    // Show success message
    Swal.fire({
      icon: 'success',
      title: isPartialImport ? 'Partial Import Successful' : 'Import Successful',
      html: successHtml,
      confirmButtonText: 'OK',
      width: isPartialImport ? '900px' : '600px'
    });

    // Refresh grid to show changes
    if (gridApi) {
      gridApi.refreshCells();
    }
  };

  // ===== END IMPORT HANDLERS =====

  // ===== COPY TABLE HANDLER (Migrated from old StudentUniform2.aspx:3324-3394) =====
  const handleCopyTable = () => {
    if (!gridApi) {
      Swal.fire(t('common:error'), t('studentUniform:errors.tableNotInitialized'), 'error');
      return;
    }

    // Define exact headers matching old application (13 columns)
    const headers = [
      "NAMA", "NOKP", "STATUSWARGANEGARA", "TAHUN", "KELAS", "JANTINA",
      "BAJU PUTIH", "SELUAR PANJANG", "SELUAR PENDEK", "BAJU KURUNG",
      "KAIN KURUNG", "PINAFORE", "KASUT"
    ];

    // Field mapping (React state uses camelCase, clipboard uses uppercase)
    const fieldMapping = {
      "NAMA": "name",
      "NOKP": "noKP",
      "STATUSWARGANEGARA": "citizenStatus",
      "TAHUN": "tahun",
      "KELAS": "class",
      "JANTINA": "jantina",
      "BAJU PUTIH": "bajuPutih",
      "SELUAR PANJANG": "seluarPanjang",
      "SELUAR PENDEK": "seluarPendek",
      "BAJU KURUNG": "bajuKurung",
      "KAIN KURUNG": "kainKurung",
      "PINAFORE": "pinafore",
      "KASUT": "kasut"
    };

    // Get all row data from AG-Grid (exclude deleted rows)
    const allRows = [];
    gridApi.forEachNode((node) => {
      if (node.data && !node.data.isDeleted) {
        allRows.push(node.data);
      }
    });

    if (allRows.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: t('common:noData'),
        text: t('studentUniform:errors.noData'),
        confirmButtonText: t('common:ok')
      });
      return;
    }

    // Create header row
    const headerString = headers.join('\t');

    // Create data rows
    const tableData = allRows.map(row => {
      return headers.map(header => {
        const field = fieldMapping[header];
        let value = row[field] || "";

        // Special handling: Tahun - use selectedTahun if row.tahun is empty (matches valueGetter logic)
        if (field === "tahun" && !value) {
          value = selectedTahun || "";
        }

        // Special handling: Add apostrophe prefix to NoKP to prevent Excel scientific notation
        if (field === "noKP") {
          return `'${value}`;
        }

        // Escape double quotes
        if (typeof value === "string" && value.includes('"')) {
          value = value.replace(/"/g, '""');
          return `"${value}"`;
        }

        return value;
      }).join('\t');
    });

    // Add header as first row
    tableData.unshift(headerString);
    const tableString = tableData.join('\n');

    // Copy to clipboard using modern Clipboard API (with legacy fallback)
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(tableString)
        .then(() => {
          showCopySuccessMessage(allRows.length);
        })
        .catch((error) => {
          console.error('Clipboard API failed, using fallback:', error);
          fallbackCopyToClipboard(tableString, allRows.length);
        });
    } else {
      // Fallback for older browsers
      fallbackCopyToClipboard(tableString, allRows.length);
    }
  };

  // Legacy clipboard fallback (for older browsers)
  const fallbackCopyToClipboard = (text, rowCount) => {
    const tempTextArea = document.createElement('textarea');
    tempTextArea.value = text;
    tempTextArea.style.position = 'fixed';
    tempTextArea.style.left = '-9999px';
    document.body.appendChild(tempTextArea);
    tempTextArea.select();

    try {
      document.execCommand('copy');
      showCopySuccessMessage(rowCount);
    } catch (error) {
      console.error('Fallback copy failed:', error);
      Swal.fire({
        icon: 'error',
        title: t('common:error'),
        text: t('studentUniform:errors.copyFailed'),
        confirmButtonText: t('common:ok')
      });
    } finally {
      document.body.removeChild(tempTextArea);
    }
  };

  // Show success message (matching old application's Malay text)
  const showCopySuccessMessage = (rowCount) => {
    Swal.fire({
      icon: 'success',
      title: t('studentUniform:copyTable.success'),
      html: `
        <div style="text-align: left; padding: 10px;">
          <p style="margin-bottom: 10px;">${t('studentUniform:copyTable.successMessage')}</p>
          <p style="margin-bottom: 10px;"><strong>${rowCount} ${t('studentUniform:copyTable.rowCount')}</strong></p>
          <ul style="margin-left: 20px; color: #666;">
            <li>${t('studentUniform:copyTable.includesHeader')}</li>
            <li>${t('studentUniform:copyTable.excludesColumns')}</li>
            <li>${t('studentUniform:copyTable.excludesDeleted')}</li>
            <li>${t('studentUniform:copyTable.icFormatted')}</li>
          </ul>
          <p style="margin-top: 10px; font-weight: 500;">${t('studentUniform:copyTable.pasteInfo')}</p>
        </div>
      `,
      confirmButtonText: t('common:ok'),
      confirmButtonColor: '#28a745',
      width: '600px'
    });
  };

  // ===== END COPY TABLE HANDLER =====

  // NOTE: Old handleSave and handleSubmit functions removed (262 lines)
  // Now using refactored hook versions from hooks/useUniformHandlers.js
  // The hook eliminates 80% code duplication between Save and Submit operations

  // Wrapper functions to call hook-based handlers with component state
  const handleSave = () => {
    handleSaveFromHook({
      students,
      selectedActivity,
      selectedTahun,
      totalStudents,
      sizeConfig,
      gridApi,
    });
  };

  const handleSubmit = () => {
    handleSubmitFromHook({
      students,
      selectedActivity,
      selectedTahun,
      totalStudents,
      sizeConfig,
      gridApi,
    });
  };

  // Handle Report Issue button click
  const handleReportIssueClick = async () => {
    try {
      // DO NOT clear diagnostic data - we want to see the user's activity trail
      // for troubleshooting both bugs and usage support requests

      Swal.fire({
        title: t('studentUniform:screenshot.capturing'),
        html: t('studentUniform:screenshot.wait'),
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const canvas = await html2canvas(document.body, {
        useCORS: true,
        allowTaint: true,
        scrollY: -window.scrollY,
        scrollX: -window.scrollX,
        width: window.innerWidth,
        height: window.innerHeight
      });

      const imageData = canvas.toDataURL('image/png');
      setScreenshot(imageData);
      setShowReportIssueModal(true);
      Swal.close();
    } catch (error) {
      console.error('Error capturing screenshot:', error);
      Swal.fire({
        icon: 'error',
        title: t('studentUniform:screenshot.captureFailed'),
        text: t('studentUniform:screenshot.captureError')
      });
    }
  };

  return (
    <div className="student-uniform-container">
      {/* Header with Logo and Title */}
      <div className="header-container">
        <img className="logo" src="/KTC.png" alt="KTC" />
        <h2 title={t('studentUniform:title', { lng: 'en' })}>
          {t('studentUniform:title')} - {selectedActivity?.activityName || ''}
        </h2>
        <div className="header-buttons">
          <button className="header-btn btn-report-issue" onClick={handleReportIssueClick} title={t('studentUniform:tooltips.reportIssue', { lng: 'en' })}>
            <i className="fas fa-bug"></i>
          </button>
        </div>
      </div>

      {/* School Info and Status Panels */}
      {schoolInfo && (
        <div className="top-section">
          {/* Left Panel - School Information */}
          <div className="left-info">
            <p><strong>DAERAH:</strong> {schoolInfo.ppd}</p>
            <p>
              <strong>DUN:</strong> {schoolInfo.dunName} ({schoolInfo.dunCode})
            </p>
            <p>
              <strong>PARLIMEN:</strong> {schoolInfo.parName} ({schoolInfo.parCode})
            </p>
            <p><strong>SEKOLAH:</strong> {schoolInfo.schoolName}</p>
            <p><strong>TAHUN PENDAFTARAN:</strong> {selectedActivity?.enrolYear || ''}</p>
          </div>

          {/* Right Panel - Status and User Info */}
          <div className="right-info">
            <p><strong>Status:</strong> <span className={`status-${status?.status?.toLowerCase()}`}>{status?.status || 'OPEN'}</span></p>
            <p><strong>Pengguna:</strong> {schoolInfo.userEmail}</p>
            <p>
              <strong>Bilangan Pelajar:</strong> <span id="studentCount">{totalStudents}</span>
              {' '}(<strong>Lelaki:</strong> <span id="maleCount">{maleStudents}</span>
              {' '}<strong>Perempuan:</strong> <span id="femaleCount">{femaleStudents}</span>)
            </p>
            <p>
              <strong>Kelompok Mengikut Kelas:</strong>
              <button
                className="btn-action blue-button"
                onClick={() => setShowClassGroupingModal(true)}
              >
                Senaraikan Infomasi Kelas
              </button>
            </p>
          </div>
        </div>
      )}

      {/* Selectors and Action Buttons Row */}
      <div className="selectors-actions-row">
        {/* Left: Selectors */}
        <div className="uniform-selectors">
          <div className="selector-group">
            <label title={t('studentUniform:filters.activity', { lng: 'en' })}>{t('studentUniform:filters.activity')}:</label>
            <select
              value={selectedActivity?.activityId || ''}
              onChange={handleActivityChange}
              disabled={isLoading}
            >
              <option value="">{t('studentUniform:dropdowns.selectActivity')}</option>
              {activities.map((activity) => (
                <option key={activity.activityId} value={activity.activityId}>
                  {activity.activityName} ({activity.enrolYear})
                </option>
              ))}
            </select>
          </div>

          <div className="selector-group">
            <label title={t('studentUniform:filters.yearGrade', { lng: 'en' })}>{t('studentUniform:filters.yearGrade')}:</label>
            <select
              value={selectedTahun || ''}
              onChange={handleTahunChange}
              disabled={isLoading}
            >
              <option value="">{t('studentUniform:dropdowns.selectTahun')}</option>
              {tahuns.map((tahun) => (
                <option key={tahun.tahun} value={tahun.tahun}>
                  {tahun.tahun}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Right: Action Buttons */}
        {selectedActivity && selectedTahun && status?.canEdit && (
          <div className="action-buttons-right">
            <button
              className="btn btn-import"
              onClick={handleImportClick}
              disabled={isLoading}
              title={t('studentUniform:buttons.importStudents', { lng: 'en' })}
            >
              {t('studentUniform:buttons.importStudents')}
            </button>

            <button
              className="btn btn-add-row"
              onClick={handleAddNewStudentRow}
              disabled={isLoading}
              title={t('studentUniform:tooltips.addStudent', { lng: 'en' })}
            >
              <i className="fas fa-plus"></i> {t('studentUniform:buttons.addStudent')}
            </button>

            <button
              className="btn btn-save"
              onClick={handleSave}
              disabled={!hasUnsavedChanges || isLoading}
              title={t('studentUniform:buttons.save', { lng: 'en' })}
            >
              {isLoading ? t('studentUniform:status.saving') : t('studentUniform:buttons.save')}
            </button>

            <button
              className="btn btn-submit"
              onClick={handleSubmit}
              disabled={isLoading}
              title={t('studentUniform:buttons.submit', { lng: 'en' })}
            >
              {isLoading ? t('studentUniform:status.submitting') : t('studentUniform:buttons.submit')}
            </button>

            <button
              className="btn btn-action"
              onClick={handleCopyTable}
              disabled={!students || students.length === 0 || isLoading}
              title={t('studentUniform:tooltips.copyTable', { lng: 'en' })}
            >
              {t('studentUniform:buttons.copyTable')}
            </button>

            {hasUnsavedChanges && (
              <span className="unsaved-indicator">{t('studentUniform:status.unsavedChanges')}</span>
            )}
          </div>
        )}
      </div>

      {/* Statistics Cards */}
      {totalStudents > 0 && (
        <div className="student-stats">
          <div className="stat-card">
            <div className="stat-label" title={t('studentUniform:stats.totalStudents', { lng: 'en' })}>{t('studentUniform:stats.totalStudents')}</div>
            <div className="stat-value">{totalStudents}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label" title={t('studentUniform:stats.maleStudents', { lng: 'en' })}>{t('studentUniform:stats.maleStudents')}</div>
            <div className="stat-value">👨 {maleStudents}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label" title={t('studentUniform:stats.femaleStudents', { lng: 'en' })}>{t('studentUniform:stats.femaleStudents')}</div>
            <div className="stat-value">👩 {femaleStudents}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label" title={t('studentUniform:stats.status', { lng: 'en' })}>{t('studentUniform:stats.status')}</div>
            <div className={`stat-value status-${status?.status?.toLowerCase()}`}>
              {status?.status || 'OPEN'}
            </div>
          </div>
        </div>
      )}

      {/* AG-Grid Table */}
      {selectedActivity && selectedTahun && (
        <div className="uniform-table-container">
          <div className="ag-theme-alpine" style={{ height: 600, width: '100%' }}>
            <AgGridReact
              theme="legacy"
              rowData={students.map(s => ({ ...s }))}  // Pass mutable copies, not Redux state directly
              columnDefs={columnDefs}
              defaultColDef={{
                sortable: true,
                filter: true,
                resizable: true,
              }}
              getRowId={(params) => params.data.studentId.toString()} // Maintain row identity to prevent scroll jumping
              onGridReady={(params) => setGridApi(params.api)}
              onCellClicked={onCellClicked}
              onCellValueChanged={onCellValueChanged}
              onCellEditingStarted={onCellEditingStarted}
              onCellEditingStopped={onCellEditingStopped}
              animateRows={true}
              rowSelection="single"
              suppressRowClickSelection={true}
              suppressScrollOnNewData={true}
              getRowClass={(params) => params.data.isDeleted ? 'row-deleted' : ''}
              getRowStyle={(params) => {
                // Zebra striping - force inline styles
                if (params.data.isDeleted) {
                  return { backgroundColor: '#ffebee' }; // Red for deleted
                }
                // Alternating colors for odd/even rows
                return params.node.rowIndex % 2 === 0
                  ? { backgroundColor: '#ffffff' } // Even rows - white
                  : { backgroundColor: '#f9f9f9' }; // Odd rows - light gray
              }}
              pagination={true}
              paginationPageSize={25}
              paginationPageSizeSelector={[25, 50, 100, 200]}
            />
          </div>
        </div>
      )}


      {/* Read-only message */}
      {selectedActivity && selectedTahun && !status?.canEdit && (
        <div className="read-only-message">
          <strong>{t('studentUniform:readOnly.title')}</strong> - {t('studentUniform:readOnly.message')}
          {t('studentUniform:info.status')}: {status?.status}
        </div>
      )}

      {/* Context Menu for Removal Reasons */}
      {contextMenu.visible && (
        <div
          className="removal-context-menu active"
          style={{
            left: `${contextMenu.x}px`,
            top: `${contextMenu.y}px`
          }}
        >
          <div className="removal-menu-header">{t('studentUniform:removalReasons.title')}</div>
          <div className="removal-menu-item" onClick={() => handleReasonSelect('LEFT_SCHOOL')}>
            <i className="fas fa-door-open"></i>
            <span>{t('studentUniform:removalReasons.leftSchool')}</span>
          </div>
          <div className="removal-menu-item" onClick={() => handleReasonSelect('GRADUATED')}>
            <i className="fas fa-graduation-cap"></i>
            <span>{t('studentUniform:removalReasons.graduated')}</span>
          </div>
          <div className="removal-menu-item" onClick={() => handleReasonSelect('TRANSFERRED')}>
            <i className="fas fa-exchange-alt"></i>
            <span>{t('studentUniform:removalReasons.transferred')}</span>
          </div>
          <div className="removal-menu-item" onClick={() => handleReasonSelect('WRONG_CLASS')}>
            <i className="fas fa-exclamation-triangle"></i>
            <span>{t('studentUniform:removalReasons.wrongClass')}</span>
          </div>
          <div className="removal-menu-item" onClick={() => handleReasonSelect('DUPLICATE')}>
            <i className="fas fa-copy"></i>
            <span>{t('studentUniform:removalReasons.duplicate')}</span>
          </div>
          <div className="removal-menu-item" onClick={() => handleReasonSelect('OTHER')}>
            <i className="fas fa-ellipsis-h"></i>
            <span>{t('studentUniform:removalReasons.other')}</span>
          </div>
        </div>
      )}

      {/* Import Modal */}
      <ImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onDataParsed={handleDataParsed}
        sizeConfig={sizeConfig}
      />

      {/* Import Preview Modal */}
      {showPreviewModal && validationResult && (
        <ImportPreviewModal
          validationResult={validationResult}
          onConfirm={handleConfirmImport}
          onCancel={() => {
            setShowPreviewModal(false);
            setValidationResult(null);
          }}
          tahuns={tahuns}
          kelasList={kelasList}
          sizeConfig={sizeConfig}
        />
      )}

      {/* Add Student Modal */}
      <AddStudentModal
        isOpen={showAddStudentModal}
        onClose={() => setShowAddStudentModal(false)}
        onAddStudent={handleConfirmAddStudent}
        enrollYear={selectedActivity?.enrolYear}
      />


      {/* Class Grouping Modal */}
      <ClassGroupingModal
        isOpen={showClassGroupingModal}
        onClose={() => setShowClassGroupingModal(false)}
        students={students}
      />

      {/* Tutorial Modal */}
      <TutorialModal
        isOpen={showTutorialModal}
        onClose={() => setShowTutorialModal(false)}
      />

      {/* Report Issue Modal */}
      {showReportIssueModal && (
        <ReportIssueModal
          context={{
            screen: t('studentUniform:context.screenName'),
            activityId: selectedActivity?.activityId,
            activityName: selectedActivity?.activityName,
            selectedTahun: selectedTahun,
            selectedActivity: selectedActivity,
            enrollmentYear: selectedActivity?.enrolYear,
            tableData: students
          }}
          screenshot={screenshot}
          onClose={() => setShowReportIssueModal(false)}
        />
      )}
    </div>
  );
};

export default StudentUniform;
